import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import {
  adTrackStyles as adTrackPropTypes,
  content as contentPropType,
} from 'lib/CustomPropTypes';
import classNames from 'classnames';

import Ad from 'components/Ad';
import BTE from 'lib/BTE';
import Breakpoints from 'lib/Breakpoints';
import TitleLogo from 'components/TitleLogo';

// TODO: remove this stylesheet when solution for grid is implemented within new theming strategy
import styles from './styles.module.scss';

class StickyAdTrack extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    adSlot: PropTypes.string,
    inFront: PropTypes.bool,
    mirror: PropTypes.bool,
    title: PropTypes.string,
    text: PropTypes.string,
    url: PropTypes.string,
    logoUrl: PropTypes.string,
    styles: adTrackPropTypes,
    content: contentPropType,
    id: PropTypes.string,
    bestOf: PropTypes.bool,
    pkgClassName: PropTypes.string,
    dataTest: PropTypes.string,
  };

  static contextTypes = {
    vertical: PropTypes.string,
  }

  static defaultProps = {
    title: null,
    text: null,
    url: null,
    logoUrl: null,
    mirror: false,
    adSlot: 'boxflex',
    styles: {},
    content: {},
    inFront: false,
    id: null,
    bestOf: false,
    pkgClassName: 'pkg sticky-ad-track',
    dataTest: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      colHeight: 0,
    };
  }

  componentDidMount() {
    BTE.on('imagesLoaded', this.setColHeight);
    BTE.monitorImagesLoad();
    BTE.on('resize', this.setColHeight);
  }

  componentWillUnmount() {
    BTE.remove('resize', this.setColHeight);
    BTE.remove('imagesLoaded', this.setColHeight);
  }

  setColHeight = () => {
    if (!this.contentColumnEl) return;

    if (Breakpoints.isSorM()) {
      this.setState({ colHeight: null });
      return;
    }

    this.setState({
      colHeight: this.contentColumnEl.clientHeight,
    });
  };

  displayTitleTextUrl() {
    const {
      bestOf, title: headline, text, url, styles: propStyles = {}, logoUrl,
    } = this.props;
    const { latestTitle: latestTitlePropStyles } = propStyles;
    if (!headline) {
      return null;
    }
    let textLink = null;
    if (url && url.length) {
      textLink = (
        <a href={url} className={classNames(styles.textLink, 'sticky-ad-track__text-link')}>
          <span className="sticky-ad-track__text-underline">
            {text && text.length ? text : i18next.t('See All')}
          </span>
        </a>
      );
    }

    const headlineWithStyles = (
      <span className={classNames('sticky-ad-track__latest-title', styles.latestTitle, latestTitlePropStyles)}>
        <TitleLogo logoUrl={logoUrl} title={headline} />
      </span>
    );

    const titleAndTextStyles = classNames(
      styles.titleAndText,
      'sticky-ad-track__title-and-text',
      {
        'sticky-ad-track__no-text-link': !textLink && !bestOf,
        'sticky-ad-track__best-of': bestOf,
      },
    );
    return (
      <h2 className={titleAndTextStyles}>
        {headlineWithStyles}
        {' '}
        {textLink}
      </h2>
    );
  }

  adColumn() {
    const { colHeight } = this.state;
    const { adSlot, styles: propStyles = {}, mirror } = this.props;
    const { adColumn: adColumnPropStyles, ad: adPropStyles } = propStyles;
    const classes = classNames(
      'sticky-ad-track__ad-column',
      adColumnPropStyles,
      'layout-grid-item grid-col-5-l grid-col-4-xl',
      { 'sticky-ad-track__ad-column--right': mirror },
    );

    return (
      <div className={classes} style={{ height: colHeight }}>
        <Ad
          slot={adSlot}
          adClass={classNames('sticky-ad-track__ad', adPropStyles)}
          sticky
          offsetViewport={adSlot === 'boxinline' ? 50 : 100}
        />
      </div>
    );
  }

  mainColumn() {
    const { children, styles: propStyles = {} } = this.props;
    const {
      contentColumn: contentColumnPropStyles,
      contentItems: contentItemsPropStyles,
    } = propStyles;
    const classes = classNames(
      'sticky-ad-track__content-column',
      contentColumnPropStyles,
      'layout-grid-item grid-col-7-l grid-col-8-xl',
    );

    return (
      <div
        className={classes}
        ref={(el) => { this.contentColumnEl = el; }}
      >
        {this.displayTitleTextUrl()}

        <div className={classNames('sticky-ad-track__content-items', contentItemsPropStyles)}>
          {children}
        </div>
      </div>
    );
  }

  rightColumn() {
    const { mirror } = this.props;

    if (mirror) {
      return this.adColumn();
    }

    return this.mainColumn();
  }

  leftColumn() {
    const { mirror } = this.props;
    if (mirror) {
      return this.mainColumn();
    }

    return this.adColumn();
  }

  render() {
    const {
      id,
      title,
      styles: propStyles = {},
      inFront,
      mirror, pkgClassName,
      children,
      dataTest,
    } = this.props;
    const {
      columnsWrapper: columnWrapperPropStyles,
      hasTitle: hasTitlePropStyles,
      container: containerPropStyles,
    } = propStyles;

    const firstPkg = children[0];
    const firstPkgI = firstPkg
      && firstPkg.props
      && firstPkg.props.content
      && firstPkg.props.content.packageIndex;

    const classes = classNames(
      styles.columnsWrapper,
      'sticky-ad-track__columns-wrapper',
      {
        'sticky-ad-track__has-title': !!title,
        'sticky-ad-track__main-column': mirror,
      },
      columnWrapperPropStyles,
      hasTitlePropStyles,
      'layout-grid-container gutter-collapse',
    );

    return (
      <div
        className={classNames(
          pkgClassName,
          'sticky-ad-track__container',
          `pkg-index-${firstPkgI}`,
        )}
        data-test={dataTest}
      >
        <section
          className={classNames(containerPropStyles, { 'sticky-ad-track__in-front': inFront })}
          data-packageid={id}
        >
          <div className={classes}>
            {this.leftColumn()}
            {this.rightColumn()}
          </div>
        </section>
      </div>
    );
  }
}

export default StickyAdTrack;
